import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Table, TableCaption, Tbody, Td, Text, Th, Thead, Tr, VisuallyHidden } from '@chakra-ui/react';

import { ComparisonStatus } from '~/features/homepage/components/comparison-status';
import {
  getComparisonCheckTone,
  HOMEPAGE_COMPARISON_COMPETITORS,
  HOMEPAGE_COMPARISON_ROWS,
} from '~/features/homepage/constants/homepage-comparison-data';

const UDACITY_COLUMN_SHADOW = '0 12px 32px rgba(23, 92, 255, 0.16)';

function isUdacityColumn(competitor: (typeof HOMEPAGE_COMPARISON_COMPETITORS)[number]) {
  return competitor === 'udacity';
}

export function HomepageComparisonTable() {
  const { t } = useTranslation();

  return (
    <Flex flexDirection='column' gap={6} width='100%'>
      <Box overflow='visible' position='relative' width='100%'>
        <Box
          borderRadius='16px'
          bottom={0}
          boxShadow={UDACITY_COLUMN_SHADOW}
          left='40%'
          pointerEvents='none'
          position='absolute'
          top={0}
          width='20%'
          zIndex={0}
          aria-hidden
        />
        <Table
          position='relative'
          variant='unstyled'
          width='100%'
          zIndex={1}
          sx={{
            borderCollapse: 'separate',
            borderSpacing: 0,
            tableLayout: 'fixed',
            th: {
              textTransform: 'none',
            },
          }}
        >
          <VisuallyHidden as={TableCaption}>{t('home.comparison.caption')}</VisuallyHidden>
          <colgroup>
            <col style={{ width: '40%' }} />
            <col style={{ width: '20%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '18%' }} />
          </colgroup>
          <Thead>
            <Tr>
              <Th scope='col'>
                <VisuallyHidden>{t('home.comparison.columns.feature')}</VisuallyHidden>
              </Th>
              {HOMEPAGE_COMPARISON_COMPETITORS.map((competitor) => {
                const isUdacity = isUdacityColumn(competitor);

                return (
                  <Th
                    key={competitor}
                    bg={isUdacity ? 'blue.500' : undefined}
                    borderBottom={isUdacity ? 'none' : '1px solid'}
                    borderColor='grey.200'
                    borderTopRadius={isUdacity ? '16px' : undefined}
                    color={isUdacity ? 'white' : 'grey.900'}
                    fontFamily='body'
                    fontSize={{ base: isUdacity ? '13px' : '11px', md: isUdacity ? '16px' : '14px' }}
                    fontWeight={isUdacity ? '700' : '600'}
                    letterSpacing='normal'
                    lineHeight={{ base: isUdacity ? '16px' : '14px', md: isUdacity ? '24px' : '20px' }}
                    px={{ base: '4px', md: '12px' }}
                    py={{ base: '12px', md: '20px' }}
                    scope='col'
                    textAlign='center'
                    textTransform='none'
                    whiteSpace='normal'
                  >
                    {t(`home.comparison.columns.${competitor}`)}
                  </Th>
                );
              })}
            </Tr>
          </Thead>
          <Tbody>
            {HOMEPAGE_COMPARISON_ROWS.map((row, rowIndex) => {
              const isLastRow = rowIndex === HOMEPAGE_COMPARISON_ROWS.length - 1;

              return (
                <Tr key={row.id}>
                  <Th
                    borderBottom={isLastRow ? 'none' : '1px solid'}
                    borderColor='grey.200'
                    color='grey.900'
                    fontFamily='body'
                    fontSize={{ base: '12px', md: '16px' }}
                    fontWeight={{ base: '500', md: '600' }}
                    letterSpacing='normal'
                    lineHeight={{ base: '16px', md: '24px' }}
                    paddingEnd={{ base: '24px', md: '8px' }}
                    paddingStart={{ base: '0', md: '8px' }}
                    py={{ base: '12px', md: '20px' }}
                    scope='row'
                    textAlign='start'
                    textTransform='none'
                    verticalAlign='middle'
                    whiteSpace='normal'
                  >
                    {t(`home.comparison.rows.${row.id}`)}
                  </Th>
                  {HOMEPAGE_COMPARISON_COMPETITORS.map((competitor) => {
                    const status = row.statuses[competitor];
                    const isUdacity = isUdacityColumn(competitor);

                    return (
                      <Td
                        key={competitor}
                        bg={isUdacity ? 'blue.50' : undefined}
                        borderBottom={isLastRow || isUdacity ? 'none' : '1px solid'}
                        borderBottomRadius={isLastRow && isUdacity ? '16px' : undefined}
                        borderColor='grey.200'
                        px={{ base: '4px', md: '12px' }}
                        py={{ base: '12px', md: '20px' }}
                        textAlign='center'
                        verticalAlign='middle'
                      >
                        <ComparisonStatus
                          label={t(`home.comparison.status.${status}`)}
                          status={status}
                          tone={getComparisonCheckTone(competitor)}
                        />
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
      <Flex
        align='center'
        display={{ base: 'flex', md: 'none' }}
        flexWrap='wrap'
        gap={4}
        justify='center'
        data-comparison-legend
      >
        <LegendItem
          label={t('home.comparison.legend.full')}
          icon={
            <ComparisonStatus label={t('home.comparison.status.full')} status='full' tone='brand' isCompact isLegend />
          }
        />
        <LegendItem
          label={t('home.comparison.legend.partial')}
          icon={
            <ComparisonStatus
              label={t('home.comparison.status.partial')}
              status='partial'
              tone='neutral'
              isCompact
              isLegend
            />
          }
        />
        <LegendItem
          label={t('home.comparison.legend.none')}
          icon={
            <ComparisonStatus
              label={t('home.comparison.status.none')}
              status='none'
              tone='neutral'
              isCompact
              isLegend
            />
          }
        />
      </Flex>
    </Flex>
  );
}

function LegendItem({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <Flex align='center' gap={2}>
      <Box aria-hidden>{icon}</Box>
      <Text color='grey.500' fontFamily='body' fontSize='12px' fontWeight='500' lineHeight='16px'>
        {label}
      </Text>
    </Flex>
  );
}
